import { useEffect, useState } from "react";
import { Container, OverlayTrigger, Tooltip } from "react-bootstrap";

import PhoneInput, { getCountries, getCountryCallingCode } from "react-phone-number-input";
import { useSelector } from "react-redux";
import styles from "../styles/settingsGeneralDetails.module.css";

import { BrandInstance } from "../utils/brandClass";
import { UsStateInstance } from "../utils/usStateClass";
import { VenueInstance } from "../utils/venueClass";

import { SettingsBooleanRadioButtons } from "./settingsBooleanRadioButtons";
import { SettingsImageField } from "./settingsImageFields";
import { SettingsDayHoursComponent } from "./settingsDayHoursComponent";

// Define weekdays for hours input
const hours = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];



// Main component for settings general details
export const SettingsGeneralDetails = () => {
  // Accessing Redux store state
  const { activeBusiness, businessType, isLoading } = useSelector((state: any) => state.business);
  const { states, isLoading: statesLoading, error } = useSelector((state: any) => state.state);

  // State for form data
  const [formData, setFormData] = useState({ ...activeBusiness });
  const [phoneNumber, setPhoneNumber] = useState<any>("");

  // state for initial form data
  const [initialFormData, setInitialFormData] = useState({ ...activeBusiness });

  // helper variables for dropdown times
  const [updates, setUpdates] = useState<Record<string, string | undefined>>({});
  const [canSave, setCanSave] = useState(false);

  const dayHourKeys = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

  // useEffect to see if the form data has changed
  useEffect(() => {
    if (JSON.stringify(formData) !== JSON.stringify(initialFormData)) {
      console.log("form data has changed");
      setCanSave(true);
    } else {
      console.log("form data has not changed");
      setCanSave(false);
    }
  }, [formData]);

  useEffect(() => {
    setFormData({
      ...formData,
      phoneNumber: phoneNumber,
    });
  }, [phoneNumber]);

  // Fetch states if not already loaded
  useEffect(() => {
    console.log("formData:", formData);
    if (states.length === 0) {
      (async () => {
        await UsStateInstance.getUsStates();
      })();
    }
  }, []);

  //useEffect for loading times in if they are set
  useEffect(() => {
    // Log the initial formData for debugging
    console.log("Initial formData:", formData);

    dayHourKeys.forEach((day) => {
      const hours = formData[`${day}Hours`]; // Use currentFormData


      if (hours) {
        console.log("PARSING OUT", hours);
        const { startTime, endTime, startAmPm, endAmPm } = parseTimeForForm(hours);
        if(startTime ==="open 24 hours" || startTime==="closed"){
          updates[`${day}Hours`] = startTime;
          delete updates[`${day}End`];
          delete updates[`${day}StartAmPm`];
          delete updates[`${day}EndAmPm`];
        }else{
          updates[`${day}Start`] = startTime;
          updates[`${day}End`] = endTime;
          updates[`${day}StartAmPm`] = startAmPm;
          updates[`${day}EndAmPm`] = endAmPm;
        }
      }
    });
    // Return the updated form data object, merging current form data with updates
    console.log("UPDATES BE", updates);
    setFormData({
      ...formData,
      ...updates,
    });
    console.log("CURRENTFORM IS NOW", formData);
  }, []);

  // Update formData when activeBusiness changes
  useEffect(() => {
    setFormData({ ...activeBusiness });
  }, [activeBusiness]);

  useEffect(() => {
    console.log("formData:", formData);
  }, [formData]);

  // Handle the form submission
  const handleSubmit = async (e: any) => {
    e.preventDefault();

    // Format times correctly before submission
    const updatedTimes: Record<string, string> = dayHourKeys.reduce((acc: Record<string, string>, day: string) => {
      const startMer: string = (updates[`${day}StartAmPm`] as string) || "AM";
      const endMer: string = (updates[`${day}EndAmPm`] as string) || "PM";
      const startTime: string = updates[`${day}Start`] ? `${updates[`${day}Start`]} ${startMer}` : "";
      const endTime: string = updates[`${day}End`] ? `${updates[`${day}End`]} ${endMer}` : "";

      console.log(`preconditional ${day}`, formData[`${day}Hours`]);
      if(formData[`${day}Hours`] =="open 24 hours" || formData[`${day}Hours`] =="closed"){
        console.log("IN UPDATES COND OPEN 24 HRS OR CLOSED",updates[`${day}Hours`]);
        acc[`${day}Hours`] = (updates[`${day}Hours`] as string);
      } else {
        // Ensure both times are non-empty before setting
        acc[`${day}Hours`] = `${startTime} - ${endTime}`;
      }
      return acc;
    }, {});

    console.log("in handle submit updatedTimes", updatedTimes);

    // filter updated times and if NaN, remove from object
    for (const key in updatedTimes) {
      if (updatedTimes[key].includes("NaN")) {
        delete updatedTimes[key];
      }
    }

    // lower case the keys
    const lowerCasedKeys = Object.keys(updatedTimes).reduce((acc: any, key: any) => {
      acc[key.toLowerCase()] = updatedTimes[key];
      return acc;
    }, {});

    console.log("lowerCasedKeys:", lowerCasedKeys);

    console.log("updatedTimes:", updatedTimes);

    const submissionData = { ...formData, ...updatedTimes };

    console.log("submissionData:", submissionData);

    // Now, submissionData contains the correctly formatted times for each day
    if (businessType === "venue") {
      const res = await VenueInstance.updateVenue(activeBusiness, submissionData);

      if (!res) {
        console.error("Something went wrong updating the venue");
        return;
      }

      setInitialFormData({ ...res });
    } else {
      console.log("update brand");
      const res = await BrandInstance.updateBrand(activeBusiness, formData);

      if (!res) {
        console.error("Something went wrong updating the brand");
        return;
      }

      setInitialFormData({ ...res });
    }
  };

  // Handle form input changes with consideration for numeric inputs
  const handleChange = (e: any) => {
    const { name, value, type } = e.target;
    // Determine if the input is meant to be handled as a number
    const isNumberInput = name.includes("Fee");

    setFormData((prev: any) => ({
      ...prev,
      [name]: type === "radio" ? value === "true" : isNumberInput ? parseFloat(value) || 0 : value,
    }));
  };

  const parseTimeForForm = (hoursString: string) => {
    if (!hoursString) return { startTime: "", endTime: "", startAmPm: "AM", endAmPm: "PM" };

    if(hoursString ==="closed" || hoursString==="open 24 hours"){
      return {startTime: hoursString, endTime:"", startAmPm:"", endAmPm:""};
    }

    // Normalize the string to ensure consistency in formatting
    const normalizedHoursString = hoursString.toUpperCase().replace(/(\d)(AM|PM)/g, "$1 $2");

    // Split into start and end times
    let [startTime, endTime] = normalizedHoursString.split("-").map((s) => s.trim());
    let startHour,
      endHour,
      startAmPm = "AM",
      endAmPm = "AM";

    if (startTime) {
      [startHour, startAmPm] = startTime.includes(" ")
        ? startTime.split(" ")
        : [startTime.slice(0, -2), startTime.slice(-2)];
      // Ensure the format is HH:MM
      startHour = startHour.includes(":") ? startHour : `${startHour.slice(0, -2)}:${startHour.slice(-2)}`;
    }

    if (endTime) {
      [endHour, endAmPm] = endTime.includes(" ") ? endTime.split(" ") : [endTime.slice(0, -2), endTime.slice(-2)];
      // Ensure the format is HH:MM
      endHour = endHour.includes(":") ? endHour : `${endHour.slice(0, -2)}:${endHour.slice(-2)}`;
    }

    return {
      startTime: startHour || "",
      endTime: endHour || "",
      startAmPm,
      endAmPm,
    };
  };

  const formatTimeInput = (inputValue: any) => {
    if(inputValue ==="closed" || inputValue==="open 24 hours"){
      return inputValue;
    }
    // First, remove any non-digit characters, including previously inserted colons
    const cleanInput = inputValue.replace(/\D/g, "");

    // Determine where to insert the colon based on the length of the cleaned input
    // Assuming valid time input is either 3 or 4 digits long
    if (cleanInput.length === 3) {
      // For times like "930" -> "9:30"
      return cleanInput.charAt(0) + ":" + cleanInput.substring(1);
    } else if (cleanInput.length === 4) {
      // For times like "1130" -> "11:30"
      return cleanInput.substring(0, 2) + ":" + cleanInput.substring(2);
    }

    return inputValue; // Return the original input if it doesn't meet the criteria or needs no change
  };

  // Handle form input changes with consideration for numeric and time inputs
  const handleTimeChange = (e: any) => {
    const { name, value, type } = e.target;

    console.log("dropdown value, name", value, name);

    const isTimeInput = name.endsWith("Start") || name.endsWith("End");
    
    //if it is a timeInput, format it unless the value is open (option for 24hr) or closed
    console.log("time in handleTimeChange",value);
    const formattedValue = isTimeInput && (value !=="open 24 hours" || value !=="closed") ? formatTimeInput(value) : value;

    //if weekdayStart is set, autofill weekdayEnd, weekdayStartAmPm, weekdayEndAmPm 
    let otherName = "";
    let day = ""
    if(name.endsWith("Start")){
      otherName = name.substring(0, name.length - 5) + "End";
      day = name.substring(0, name.length - 5)
    } else if(name.endsWith("End")){
      otherName = name.substring(0, name.length - 3) + "Start";
      day = name.substring(0, name.length - 3);
    }
    
    
    //update form with usual data
    updates[name] = value;
    setFormData((prev: any) => ({
      ...prev,
      [name]: type === "radio" ? value === "true" : isTimeInput ? formattedValue : value,
    }));
  };

  // Ensure you have a correct event handler for the select element
  const handleUsStateChange = (e: any) => {
    const { value } = e.target;
    setFormData((prevFormData: any) => ({
      ...prevFormData,
      stateId: Math.floor(value),
    }));
  };

  const renderImageFields = () => (
    <>
      <SettingsImageField
        title="Avatar Image"
        value="avatarUrl"
        formData={formData}
        setFormData={setFormData}
        description="This image will be displayed as your avatar on the platform"
        dims={"600"}
        height={"600"}
      />
      <SettingsImageField
        title="Display Image"
        value="displayUrl"
        formData={formData}
        setFormData={setFormData}
        description="This image will be displayed on your business profile page"
        dims={"1080"}
        height={"1080"}
      />
    </>
  );

  // Custom input fields for form
  const renderFormField = (label: string, name: string, type = "text", placeholder = "") => (
    <div style={{ marginTop: 20 }} className={styles.categorySelect}>
      <label htmlFor={name}>{label}</label>
      {type !== "textarea" ? (
        <input type={type} name={name} value={formData[name] || ""} placeholder={placeholder} onChange={handleChange} />
      ) : (
        <textarea
          name={name}
          id={name}
          cols={30}
          rows={10}
          value={formData[name] || ""}
          onChange={handleChange}
        ></textarea>
      )}
    </div>
  );

  const renderSelectField = (label: string, name: string, options: any) => (
    <div style={{ marginTop: 20 }} className={styles.categorySelect}>
      <label htmlFor={name}>{label}</label>
      {!statesLoading ? (
        <select name={name} id={name} value={formData[name] || activeBusiness[name]} onChange={handleChange}>
          {options.map((option: any, index: any) => (
            <option key={index} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <p>Loading...</p>
      )}
    </div>
  );

  const renderStateSelectField = () => (
    <>
      {/* STATE */}
      <div style={{ marginTop: 20 }} className={styles.categorySelect}>
        <label htmlFor="">State</label>
        {!statesLoading ? (
          <select
            name="stateId"
            id="stateId"
            value={formData.stateId || activeBusiness.stateId}
            onChange={handleUsStateChange}
          >
            {states &&
              states.map((item: any) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
          </select>
        ) : (
          <p>Loading...</p>
        )}
      </div>
    </>
  );

  const renderPhoneFields = () => {
    // console.log('getcountries', getCountries());
    return (
      <div style={{ marginTop: 20 }} className={styles.categorySelect}>
        <label htmlFor={phoneNumber}>Phone Number</label>
        <PhoneInput
          countrySelectProps={{ unicodeFlags: true }}
          // remove the country select
          countrySelectComponent={({ value, flags, onSelect }) => {
            return (
              <div>
                <span></span>
              </div>
            );
          }}
          country="US"
          defaultCountry="US"
          placeholder="Phone Number"
          value={formData?.phoneNumber || ""}
          onChange={setPhoneNumber}
        />
      </div>
    );
  };

  const renderHoursFields = () => {
    return dayHourKeys.map((day) => {
      return <SettingsDayHoursComponent
      formData={formData}
      updates={updates}
      setUpdates={setUpdates}
      setFormData = {setFormData}
      day={day}
      handleTimeChange = {handleTimeChange}
      isExperience={false}
      />;
    });
  };

  // Generate form fields based on business type
  const renderFields = () =>
    // Venue specific fields
    businessType === "venue"
      ? [
          renderFormField("Venue Name", "name"),
          renderFormField("Description", "description", "textarea"),
          renderFormField("License Number", "licenseNumber"),
          renderPhoneFields(),
          renderHoursFields(),
          renderSelectField("Dispensary Type", "dispensaryType", [
            { value: "medical", label: "Medical" },
            { value: "recreational", label: "Recreational" },
            { value: "both", label: "Both" },
          ]),
          renderFormField("Street", "street"),
          renderFormField("City", "city"),
          renderFormField("Zip Code", "zip"),
          renderStateSelectField(),
          <SettingsBooleanRadioButtons
            fieldName="hasDelivery"
            formData={formData}
            label="Do you offer delivery?"
            hasFee={true}
            backendBoolField="hasDelivery"
            backendFeeField="deliveryFee"
            setFormData={setFormData}
          />,
          <SettingsBooleanRadioButtons
            fieldName="orderAhead"
            formData={formData}
            label="Do you allow customers to order ahead of time?"
            setFormData={setFormData}
            hasFee={false}
            backendBoolField="orderAhead"
            backendFeeField=""
          />,
          <SettingsBooleanRadioButtons
            fieldName="atm"
            formData={formData}
            label="Do you have an atm?"
            setFormData={setFormData}
            hasFee={true}
            backendBoolField="atm"
            backendFeeField="atmFee"
          />,
          <SettingsBooleanRadioButtons
            fieldName="debit"
            formData={formData}
            label="Do you accept debit?"
            setFormData={setFormData}
            hasFee={true}
            backendBoolField="acceptDebit"
            backendFeeField="debitFee"
          />,
          <SettingsBooleanRadioButtons
            fieldName="medicalRecordsRequired"
            formData={formData}
            label="Are medical records required?"
            setFormData={setFormData}
            hasFee={false}
            backendBoolField="medicalRecordsRequired"
            backendFeeField=""
          />,
        ]
      : // Brand specific fields
        [
          renderFormField("Brand Name", "name"),
          renderFormField("Description", "description", "textarea"),
          renderFormField("License Number", "licenseNumber"),
          renderFormField("Website Url", "website", "text", "https://www.mywebsite.com"),
          renderFormField("Tag Line", "tagLine"),
          renderFormField("Date Founded", "dateFounded", "date"),
          renderStateSelectField(),
        ];

  if (isLoading) {
    return <p>Loading...</p>;
  }

  return (
    <Container fluid>
      <h2>General Details</h2>
      <div className={styles.formWrapper}>
        <div className={styles.formLeft}>{renderImageFields()}</div>
        <div className={styles.formRight}>
          <div className={styles.borderWrapper}>
            <h4 className={styles.innerHead}>{businessType === "venue" ? "Venue" : "Brand"} Details</h4>
            <div style={{ width: "100%", height: 1, backgroundColor: "#ccc" }}></div>
            {renderFields()}
            <div style={{ justifyContent: "flex-end", marginTop: 30 }} className={styles.searchFilterWrapper}>
              <button
                style={{
                  backgroundColor: canSave ? "#0F9547" : "#ccc",
                  border: "1px solid transparent",
                  color: "#fff",
                  padding: "10px 100px",
                  borderRadius: 50,
                }}
                onClick={handleSubmit}
                disabled={!canSave}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};
